import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { FileDown, Loader2, Database, Users2, PlugZap, CheckCircle2, XCircle } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

/**
 * AdminDataExportsCard
 * ─────────────────────
 * Data-science oriented dashboard tile: gives the admin one place to
 * pull two label sources out of the platform.
 *
 * 1. Injury-events CSV — the labels for risk_v2 (feature + label).
 * 2. Marketing leads CSV — segment landing-page captures.
 *
 * Both hit REACT_APP_BACKEND_URL directly using the httpOnly session
 * cookie so the browser handles the file download properly (fetch +
 * blob rather than an <a href> because the endpoints are cookie-auth
 * and cross-origin-safe with credentials: 'include').
 */
export default function AdminDataExportsCard() {
  const [summary, setSummary] = useState(null);
  const [crm, setCrm] = useState(null);
  const [crmTesting, setCrmTesting] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const BACKEND = BACKEND_URL;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/injury-events/summary");
        setSummary(data);
      } catch { /* silent */ }
      try {
        const { data: c } = await api.get("/leads/crm-config");
        setCrm(c);
      } catch { /* silent */ }
    })();
  }, []);

  const testCrm = async () => {
    setCrmTesting(true);
    try {
      const { data } = await api.post("/leads/crm-test");
      const wp = data.whitepaper_email || {};
      const parts = [];
      if (data.forwarded) parts.push(`CRM (${data.provider}) forwarded`);
      if (wp.sent_whitepaper) parts.push("whitepaper delivered");
      if (wp.sent_notify) parts.push("founder notified");
      toast.success(
        parts.length
          ? `Test complete — ${parts.join(", ")}`
          : `Test lead fired · nothing configured to receive (see status pills)`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setCrmTesting(false);
    }
  };

  const downloadCsv = async (path, name) => {
    setDownloading(name);
    try {
      const res = await fetch(`${BACKEND}/api${path}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.headers.get("Content-Disposition")?.match(/filename="?([^"]+)"?/)?.[1] || `${name}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Download failed: ${e.message}`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <section
      className="rounded-2xl ink-card p-5 md:p-6"
      data-testid="admin-data-exports"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
            <Database className="h-3.5 w-3.5 text-fuchsia-400" /> Data exports · label pipeline
          </div>
          <div className="mt-1 font-display font-black text-xl text-white tracking-tight">
            risk_v2 training set + marketing pipeline
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Injury events */}
        <div
          className="rounded-xl border p-4 flex flex-col"
          style={{ backgroundColor: "rgba(15,30,54,0.5)", borderColor: "rgba(255,255,255,0.08)" }}
          data-testid="admin-export-injury-events"
        >
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45">
            Injury events
          </div>
          <div className="mt-1 font-display font-black text-lg text-white tracking-tight">
            {summary?.total ?? "—"} labeled events
          </div>
          {summary?.by_severity?.length > 0 && (
            <div className="mt-3 text-xs text-white/55 flex flex-wrap gap-x-3 gap-y-1">
              {summary.by_severity.map((r) => (
                <span key={r._id || "unk"}>
                  <span className="capitalize">{r._id || "unspecified"}</span>{" "}
                  <span className="font-mono-num text-white">{r.n}</span>
                </span>
              ))}
            </div>
          )}
          <div className="mt-4 flex-1" />
          <Button
            onClick={() => downloadCsv("/admin/injury-events/export.csv?days=1825", "injury-events")}
            disabled={downloading === "injury-events"}
            className="text-white font-bold w-full"
            style={{ backgroundColor: "var(--brand-secondary)" }}
            data-testid="admin-export-injury-events-btn"
          >
            {downloading === "injury-events" ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Downloading…</>
            ) : (
              <><FileDown className="h-4 w-4 mr-2" /> Download injury-events CSV</>
            )}
          </Button>
          <div className="mt-3 text-[10px] text-white/40 tracking-[0.15em] uppercase">
            Feeds risk_v2 · features + label per event
          </div>
        </div>

        {/* Marketing leads */}
        <div
          className="rounded-xl border p-4 flex flex-col"
          style={{ backgroundColor: "rgba(15,30,54,0.5)", borderColor: "rgba(255,255,255,0.08)" }}
          data-testid="admin-export-leads"
        >
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
            <Users2 className="h-3.5 w-3.5" /> Marketing leads
          </div>
          <div className="mt-1 font-display font-black text-lg text-white tracking-tight">
            Segment page captures
          </div>

          {/* CRM wiring status */}
          {crm && (
            <>
              <div className="mt-3 rounded-lg border px-3 py-2 flex items-center justify-between gap-2"
                style={{
                  backgroundColor: crm.url_configured || crm.resend_configured ? "rgba(16,185,129,0.10)" : "rgba(255,255,255,0.04)",
                  borderColor: crm.url_configured || crm.resend_configured ? "rgba(16,185,129,0.35)" : "rgba(255,255,255,0.10)",
                }}
                data-testid="admin-crm-status"
              >
                <div className="flex items-center gap-2 text-[11px]">
                  <PlugZap className="h-3.5 w-3.5" style={{ color: crm.url_configured || crm.resend_configured ? "#10B981" : "#94A3B8" }} />
                  <span className="uppercase tracking-[0.2em] font-bold text-white/60">CRM · {crm.provider}</span>
                  {crm.url_configured || crm.resend_configured ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-white/30" />
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={testCrm} disabled={crmTesting}
                  className="h-7 text-[11px] border-white/15 text-white/80 bg-white/5 hover:bg-white/10"
                  data-testid="admin-crm-test-btn"
                >
                  {crmTesting ? "Testing…" : "Test webhook + email"}
                </Button>
              </div>

              {/* Whitepaper email pipeline status */}
              {crm.whitepaper_email && (
                <div className="mt-2 rounded-lg border px-3 py-2"
                  style={{
                    backgroundColor: crm.whitepaper_email.resend_key_present && crm.whitepaper_email.pdf_present ? "rgba(16,185,129,0.10)" : "rgba(255,255,255,0.04)",
                    borderColor: crm.whitepaper_email.resend_key_present && crm.whitepaper_email.pdf_present ? "rgba(16,185,129,0.35)" : "rgba(255,255,255,0.10)",
                  }}
                  data-testid="admin-whitepaper-status"
                >
                  <div className="flex items-center gap-2 text-[11px]">
                    <FileDown className="h-3.5 w-3.5" style={{ color: crm.whitepaper_email.resend_key_present && crm.whitepaper_email.pdf_present ? "#10B981" : "#94A3B8" }} />
                    <span className="uppercase tracking-[0.2em] font-bold text-white/60">Whitepaper email</span>
                    {crm.whitepaper_email.resend_key_present && crm.whitepaper_email.pdf_present ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-white/30" />
                    )}
                  </div>
                  <div className="mt-1.5 text-[10px] text-white/50 leading-relaxed">
                    PDF · <span className="text-white/75 font-mono-num">{crm.whitepaper_email.pdf_size_kb} KB</span>
                    <span className="mx-2 text-white/25">|</span>
                    Notify · <span className="text-white/75">{crm.whitepaper_email.notify_to || "—"}</span>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="mt-3 text-xs text-white/55">
            High-school · collegiate · professional whitepaper leads with IP + role + interest.
          </div>
          <div className="mt-4 flex-1" />
          <Button
            onClick={() => downloadCsv("/leads/export.csv", "leads")}
            disabled={downloading === "leads"}
            className="text-white font-bold w-full"
            style={{ backgroundColor: "var(--brand-secondary)" }}
            data-testid="admin-export-leads-btn"
          >
            {downloading === "leads" ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Downloading…</>
            ) : (
              <><FileDown className="h-4 w-4 mr-2" /> Download leads CSV</>
            )}
          </Button>
          <div className="mt-3 text-[10px] text-white/40 tracking-[0.15em] uppercase">
            Segment-tagged for sales pipeline slicing
          </div>
        </div>
      </div>
    </section>
  );
}
